import { SharedState } from '$utils/sharedState.svelte';
import { stateGlobalClock } from '$states/globalClock.svelte';
import dayjs from 'dayjs';

type Type = Record<string, string>;

export type DelayedTransactionType =
  | 'gnosis-pay-spend-limit'
  | 'gnosis-pay-withdraw'
  | 'gnosis-pay-owner-adding'
  | 'gnosis-pay-owner-removing'
  | 'monerium-auth';

class StateDelayedTransactionCountdowns extends SharedState<Type> {
  get hasActiveCountdown() {
    if (!this.current) {
      return false;
    }

    return Object.keys(this.current).some((key) => {
      const countdown = this.current?.[key];
      return countdown && dayjs(countdown).isAfter(dayjs());
    });
  }

  constructor() {
    super(
      {},
      {
        persistence: { key: 'delayedTransactionsCountdowns' },
      },
    );
  }

  byKey = (key: DelayedTransactionType) => {
    if (!this.current) {
      return null;
    }

    return this.current[key];
  };

  cleanUpFromExpiredEntries = () => {
    if (!stateGlobalClock.current || !this.current) {
      return;
    }

    const now = stateGlobalClock.current;

    const values = {} as Type;

    Object.keys(this.current).forEach((key) => {
      const countdown = this.current?.[key];

      if (!countdown || dayjs(countdown).isBefore(now)) {
        return;
      }

      values[key as DelayedTransactionType] = countdown;
    });

    this.set(values);
  };

  add = (key: DelayedTransactionType, date = dayjs().add(3, 'minutes')) => {
    if (!this.current) {
      return;
    }

    if (key === 'monerium-auth') {
      //For monerium auth, we need to add 30s for monerium to process the request
      this.set({
        ...this.current,
        [key]: dayjs().add(3.5, 'minutes').toISOString(),
      });
    } else {
      this.set({
        ...this.current,
        [key]: date.toISOString(),
      });
    }
  };

  isActive = (key: DelayedTransactionType) => {
    if (!this.current) {
      return;
    }

    const countdown = this.current[key];

    if (!countdown) {
      return false;
    }

    const now = stateGlobalClock.current;

    return dayjs(countdown).isAfter(now);
  };
}

export const stateDelayedTransactionCountdowns = new StateDelayedTransactionCountdowns();
